import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { LANGUAGES } from '@/constants/languages';
import { Languages, ChevronDown, Menu, X } from 'lucide-react';
import { useCmsPage, isFooterSettings } from '@/hooks/useCmsPage';
import type { CmsComponent } from '@/services/cms-types';
import type { Language } from '@/i18n/types';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { SplashHero } from '@/components/splash/SplashHero';
import { SignupSteps } from '@/components/splash/SignupSteps';
import { SignupStep } from '@/components/splash/SignupStep';
import { SignupCta } from '@/components/splash/SignupCta';
import { SignInCard } from '@/components/splash/SignInCard';

/**
 * Default SEO copy used when the CMS does not supply page metadata.
 * Matches the splash page build spec.
 */
const DEFAULT_SEO = {
  title: 'Sign up for Data Mission: Sync',
  description:
    "Create your Data Mission account in about two minutes. Here's what to expect from the four-step signup before you start.",
  keywords:
    'data mission sync signup, dms signup, datamission signup, data mission account, dataverse sync signup, business central sync signup, dms free trial, data mission free trial, dms signup help, datamission account creation',
} as const;

/**
 * Path to the brand illustration shared with the rest of the marketing site.
 */
const HERO_ILLUSTRATION = '/images/cloud-connect.png';

/**
 * Props for the LandingPage component.
 */
interface LandingPageProps {
  /** Handler for the sign-in flow (existing Entra sign-in). */
  onSignIn: () => void;
  /** Handler for the email-based sign-up flow (existing Entra signup). */
  onSignUp: () => void;
}

/**
 * Creates a sanitized markup object for safe use with `dangerouslySetInnerHTML`.
 * All CMS HTML content must pass through this function before rendering.
 */
function createSanitizedMarkup(html: string): { __html: string } {
  return { __html: sanitizeHtml(html) };
}

/**
 * Splash / landing page shown to unauthenticated users.
 * Prepares the user for the four-step Microsoft Entra signup flow before
 * handing them off, so the Entra-styled screens don't feel like a wrong turn.
 *
 * Localized via the LanguageContext (en / fr / de / es). The footer copy and
 * SEO metadata are still sourced from the CMS when available, with sensible
 * defaults from the splash build spec.
 */
export default function LandingPage({ onSignIn, onSignUp }: LandingPageProps) {
  const { t } = useLanguage();
  const { findFooterComponent, seo } = useCmsPage('/');

  const signInLabel = t.landing.signIn;
  const signUpLabel = t.landing.signUp;

  // Apply SEO metadata to the document head, falling back to spec defaults
  // when the CMS does not provide a value.
  useEffect(() => {
    document.title = seo?.metaTitle ?? DEFAULT_SEO.title;
    updateMetaTag('description', seo?.metaDescription ?? DEFAULT_SEO.description);
    updateMetaTag('keywords', seo?.metaKeywords ?? DEFAULT_SEO.keywords);

    if (seo?.ogTitle) {
      updateMetaTag('og:title', seo.ogTitle, 'property');
    }
    if (seo?.ogDescription) {
      updateMetaTag('og:description', seo.ogDescription, 'property');
    }
    if (seo?.ogImage) {
      updateMetaTag('og:image', seo.ogImage, 'property');
    }
    if (seo?.twitterCard) {
      updateMetaTag('twitter:card', seo.twitterCard);
    }
    if (seo?.canonicalUrl) {
      updateCanonicalLink(seo.canonicalUrl);
    }
  }, [seo]);

  // Step content sourced from i18n; rendered as a stack inside the shared
  // 880px-wide content column.
  const stepLabel = t.landing.steps.stepLabel;
  const steps = [
    { title: t.landing.steps.step1Title, body: t.landing.steps.step1Body },
    { title: t.landing.steps.step2Title, body: t.landing.steps.step2Body },
    { title: t.landing.steps.step3Title, body: t.landing.steps.step3Body },
    { title: t.landing.steps.step4Title, body: t.landing.steps.step4Body },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f3]">
      {/* Header — brand mark, sign-in link, sign-up pill, language picker */}
      <LandingHeader
        onSignIn={onSignIn}
        onSignUp={onSignUp}
        signInLabel={signInLabel}
        signUpLabel={signUpLabel}
      />

      <main className="flex-1">
        {/* Dark hero band */}
        <SplashHero
          eyebrow={t.landing.hero.eyebrow}
          title={t.landing.hero.title}
          subhead={t.landing.hero.subhead}
          illustrationSrc={HERO_ILLUSTRATION}
          illustrationAlt={t.landing.hero.illustrationAlt}
        />

        {/* Steps + CTA + sign-in alternative all share the 880px column */}
        <div className="max-w-[880px] mx-auto px-6 sm:px-8 py-12 md:py-16 flex flex-col gap-6">
          <SignupSteps>
            {steps.map((step, index) => (
              <SignupStep
                key={step.title}
                number={index + 1}
                stepLabel={stepLabel}
                title={step.title}
                body={step.body}
              />
            ))}
          </SignupSteps>

          <SignupCta
            headline={t.landing.cta.headline}
            subhead={t.landing.cta.subhead}
            signUpEmailLabel={t.landing.cta.signUpEmail}
            signUpMicrosoftLabel={t.landing.cta.signUpMicrosoft}
            microsoftIconAlt={t.landing.cta.microsoftIconAlt}
            onSignUpEmail={onSignUp}
            onSignUpMicrosoft={onSignUp}
          />

          <SignInCard
            text={t.landing.signInCard.text}
            linkLabel={t.landing.signInCard.link}
            onSignIn={onSignIn}
          />
        </div>
      </main>

      {/* Yellow-band footer — CMS-driven copyright, privacy policy, terms */}
      <LandingFooter footerComponent={findFooterComponent('Footer')} />
    </div>
  );
}

/**
 * Props for the splash header.
 */
interface LandingHeaderProps {
  onSignIn: () => void;
  onSignUp: () => void;
  signInLabel: string;
  signUpLabel: string;
}

/**
 * Sticky splash header. White background, brand mark on the left, in-page
 * CTA duplicates on the right (sign-in link, sign-up pill, language picker).
 * Mobile collapses the right-hand controls into a hamburger menu.
 */
function LandingHeader({
  onSignIn,
  onSignUp,
  signInLabel,
  signUpLabel,
}: LandingHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
      className="bg-white sticky top-0 z-50"
      style={{ borderBottom: '0.5px solid rgba(36,36,36,0.12)' }}
    >
      <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between lg:justify-start h-[100px]">
          {/* Brand mark */}
          <div className="flex items-center shrink-0">
            <img
              src="/images/MIP-FullLogo.svg"
              alt="Mission Impact Partners"
              className="h-[72px] lg:h-[80px] w-auto"
            />
          </div>

          {/* Desktop CTAs (lg+) */}
          <div className="hidden lg:flex items-center flex-1 ml-16">
            <div className="flex items-center flex-1 justify-end gap-6">
              <button
                onClick={onSignIn}
                className="text-[#242424] hover:text-[#ff7e1a] transition-colors cursor-pointer text-[17px] font-semibold"
                style={{ fontFamily: '"Urbanist", "Helvetica", sans-serif' }}
              >
                {signInLabel}
              </button>

              {/* Yellow pill — uppercases the label per spec */}
              <button
                onClick={onSignUp}
                className="bg-[#f5ea29] hover:bg-[#e0d600] text-[#242424] px-6 py-3 font-bold cursor-pointer transition-colors text-[13px] uppercase rounded-full"
                style={{
                  fontFamily: '"Urbanist", "Helvetica", sans-serif',
                  letterSpacing: '0.5px',
                }}
              >
                {signUpLabel}
              </button>

              <LanguageSelectorDesktop />
            </div>
          </div>

          {/* Mobile menu toggle (<lg) */}
          <button
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); }}
            className="lg:hidden p-2 cursor-pointer"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-[#242424]" />
            ) : (
              <Menu className="w-6 h-6 text-[#242424]" />
            )}
          </button>
        </div>

        {/* Mobile menu (<lg) */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-6">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onSignIn();
                }}
                className="bg-[#1a1a1a] hover:bg-[#333] text-white px-8 py-3 font-bold text-center cursor-pointer transition-colors text-[13px] uppercase rounded-full"
                style={{
                  fontFamily: '"Urbanist", "Helvetica", sans-serif',
                  letterSpacing: '0.5px',
                }}
              >
                {signInLabel}
              </button>

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onSignUp();
                }}
                className="bg-[#f5ea29] hover:bg-[#e0d600] text-[#242424] px-8 py-3 font-bold text-center cursor-pointer transition-colors text-[13px] uppercase rounded-full"
                style={{
                  fontFamily: '"Urbanist", "Helvetica", sans-serif',
                  letterSpacing: '0.5px',
                }}
              >
                {signUpLabel}
              </button>

              <LanguageSelectorMobile />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

/**
 * Desktop language selector: globe icon + current code + dropdown.
 */
function LanguageSelectorDesktop() {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); }}
        className="flex items-center gap-1 px-3 py-2 text-sm text-[#242424] hover:text-[#ff7e1a] transition-colors cursor-pointer"
        aria-label="Select language"
      >
        <Languages className="h-5 w-5" />
        <span className="uppercase font-medium">{language}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white shadow-lg border border-gray-100 rounded-md min-w-[140px] py-1 z-50">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setOpen(false);
              }}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors cursor-pointer ${
                lang.code === language ? 'font-bold text-[#ff7e1a]' : 'text-[#242424]'
              }`}
            >
              {lang.nativeName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Mobile language selector: native <select> at the bottom of the menu.
 */
function LanguageSelectorMobile() {
  const { language, setLanguage } = useLanguage();

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <label
        htmlFor="mobile-language-select"
        className="block text-sm font-medium text-[#242424] mb-1"
      >
        Language
      </label>
      <select
        id="mobile-language-select"
        value={language}
        onChange={(e) => { setLanguage(e.target.value as Language); }}
        className="w-full p-2 border border-gray-200 rounded bg-white text-[#242424]"
      >
        {LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.nativeName}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Updates or creates a meta tag in the document head.
 */
function updateMetaTag(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attr}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

/**
 * Updates or creates the canonical link in the document head.
 */
function updateCanonicalLink(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * Yellow-band footer with CMS-driven copyright + URLs and i18n link labels.
 * The CMS only stores English link text, so the labels come from the active
 * locale's translations to keep the footer in sync with the language picker.
 * Renders nothing if the CMS footer component is unavailable.
 */
function LandingFooter({ footerComponent }: { footerComponent: CmsComponent | undefined }) {
  const { t } = useLanguage();

  if (!footerComponent || !isFooterSettings(footerComponent.settings)) {
    return null;
  }

  const { copyright, privacyPolicyUrl, termsOfUseUrl } = footerComponent.settings;

  return (
    <footer
      className="bg-[#f5ea29] py-4"
      style={{ fontFamily: '"Urbanist", "Helvetica", sans-serif' }}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-[#1A1A1A] text-sm">
        <span dangerouslySetInnerHTML={createSanitizedMarkup(copyright)} />
        <div className="flex items-center gap-4">
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {t.landing.footer.privacyPolicy}
          </a>
          <span aria-hidden="true">|</span>
          <a
            href={termsOfUseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {t.landing.footer.termsOfUse}
          </a>
        </div>
      </div>
    </footer>
  );
}
